import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { useVendorAppointments } from "./appointments.hooks";
import AppointmentStatusBadge from "@/app/pmc/appointments/components/AppointmentStatusBadge";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { appointment_status } from "@/models/appointment.model";
import VendorAppointmentActionsDropdown from "./components/VendorAppointmentActionsDropdown";
import BillingExceptionNote from "./components/BillingExceptionNote";
import BookingSheet from "./components/BookingSheet";
import BulkUploadModal from "./components/BulkUploadModal";
import AppointmentQuickViewSheet from "@/app/pmc/appointments/components/AppointmentQuickViewSheet";
import { Button } from "@/components/ui/button";
import { Upload, Eye } from "lucide-react";
import moment from "moment";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { AppointmentMetaData } from "@/models/appointment.model";

export default function VendorAppointmentsPage() {
  const {
    list,
    total,
    filters,
    options,
    pagination,
    individuals,
    subs,
    updateAppointmentInList,
    sheetOpen,
    openSheet,
    closeSheet,
    refetch,
  } = useVendorAppointments();
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [quickViewAppt, setQuickViewAppt] = useState<AppointmentJoin | null>(null);

  // ── URL filter persistence ─────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();

  // On mount: hydrate Redux filters from URL params
  useEffect(() => {
    const q        = searchParams.get("q");
    const status   = searchParams.get("status");
    const service  = searchParams.get("service");
    const from     = searchParams.get("from");
    const to       = searchParams.get("to");
    const assignee = searchParams.get("assignee");

    if (q)        filters.setSearch(q);
    if (status)   filters.setStatus(status);
    if (service)  filters.setServiceParent(service);
    if (from)     filters.setDateFrom(from);
    if (to)       filters.setDateTo(to);
    if (assignee) filters.setAssignee(assignee);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync whenever filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search)        params.set("q",        filters.search);
    if (filters.status)        params.set("status",   filters.status);
    if (filters.serviceParent) params.set("service",  filters.serviceParent);
    if (filters.dateFrom)      params.set("from",     filters.dateFrom);
    if (filters.dateTo)        params.set("to",       filters.dateTo);
    if (filters.assignee)      params.set("assignee", filters.assignee);
    setSearchParams(params, { replace: true });
  }, [
    filters.search,
    filters.status,
    filters.serviceParent,
    filters.dateFrom,
    filters.dateTo,
    filters.assignee,
  ]);

  // ── Date helpers ───────────────────────────────────────────────────────
  const dateFrom: Date | undefined = useMemo(() => {
    return filters.dateFrom ? moment(filters.dateFrom, "YYYY-MM-DD").toDate() : undefined;
  }, [filters.dateFrom]);

  const dateTo: Date | undefined = useMemo(() => {
    return filters.dateTo ? moment(filters.dateTo, "YYYY-MM-DD").toDate() : undefined;
  }, [filters.dateTo]);

  const handleDateFromChange = (date: Date | undefined) => {
    filters.setDateFrom(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  const handleDateToChange = (date: Date | undefined) => {
    filters.setDateTo(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  const getDisabledDatesForFrom = (date: Date): boolean => {
    if (dateTo) return moment(date).isAfter(moment(dateTo), "day");
    return false;
  };

  const getDisabledDatesForTo = (date: Date): boolean => {
    if (dateFrom) return moment(date).isBefore(moment(dateFrom), "day");
    return false;
  };

  // ── Filters ────────────────────────────────────────────────────────────
  const filterItems: FilterItem[] = useMemo(
    () => [
      {
        type: "search",
        title: "Search by property, unit, or service...",
        value: filters.search,
        onValueChange: filters.setSearch,
        grid: 3,
      },
      {
        type: "date",
        title: "From Date",
        value: dateFrom,
        onValueChange: handleDateFromChange,
        grid: 2,
        disabledDates: getDisabledDatesForFrom,
      },
      {
        type: "date",
        title: "To Date",
        value: dateTo,
        onValueChange: handleDateToChange,
        grid: 2,
        disabledDates: getDisabledDatesForTo,
      },
      {
        type: "select",
        title: "Status",
        value: filters.status,
        onValueChange: filters.setStatus,
        options: [
          { value: "pending",     label: "Pending" },
          { value: "assigned",    label: "Assigned" },
          { value: "accepted",    label: "Accepted" },
          { value: "inprogress",  label: "In Progress" },
          { value: "goback",      label: "Go Back" },
          { value: "rescheduled", label: "Rescheduled" },
          { value: "inspected",   label: "Inspected" },
          { value: "done",        label: "Done" },
          { value: "completed",   label: "Completed" },
          { value: "cancelled",   label: "Cancelled" },
        ],
        grid: 2,
      },
      {
        type: "select",
        title: "Service",
        value: filters.serviceParent,
        onValueChange: filters.setServiceParent,
        options: options.serviceParents.filter((p) => p && p.trim()).map((p) => ({ label: p, value: p })),
        grid: 2,
      },
      {
        type: "combobox",
        title: "Assignee",
        value: filters.assignee,
        onValueChange: filters.setAssignee,
        options: [
          ...individuals.map((e) => ({ label: `${e.name} (Individual)`, value: e.name })),
          ...subs.map((s) => ({ label: `${s.name} (Sub)`, value: s.name })),
        ],
        grid: 3,
      },
    ],
    [
      filters.search,
      filters.status,
      filters.serviceParent,
      filters.assignee,
      filters.dateFrom,
      filters.dateTo,
      options.serviceParents,
      individuals,
      subs,
    ]
  );

  // ── Columns ────────────────────────────────────────────────────────────
  const cols = useMemo<ColumnDef<AppointmentJoin>[]>(
    () => [
      {
        header: "ID",
        accessor: (r) => {
          const idDisplay = r.id.slice(0, 8).toUpperCase();
          const handleCopy = async (e: React.MouseEvent) => {
            e.stopPropagation();
            try {
              await navigator.clipboard.writeText(r.id);
              toast.success("ID copied to clipboard");
            } catch {
              toast.error("Failed to copy ID");
            }
          };
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={handleCopy} className="font-mono cursor-pointer hover:underline text-left">
                  {idDisplay}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Click to copy</p>
              </TooltipContent>
            </Tooltip>
          );
        },
        sortable: true,
        sortKey: "id",
      },
      {
        header: "Property",
        accessor: (r) => (
          <Link to={`/appointments/${r.id}`} className="flex flex-col hover:underline">
            <span className="font-medium">{r.property.name}</span>
            <span className="text-sm text-muted-foreground">
              Unit {r.property.unit_number} • Beds {r.property.beds} • Baths {r.property.baths}
            </span>
          </Link>
        ),
        sortable: true,
        sortKey: "properties.name",
        cellClassName: "hover:bg-purple-50 dark:hover:bg-purple-900",
      },
      {
        header: "Assign Individual",
        accessor: (r) => (
          <VendorAppointmentActionsDropdown
            appointment={r}
            type="assignment"
            onAppointmentUpdate={updateAppointmentInList}
            onRefetch={refetch}
          />
        ),
      },
      {
        header: "Service",
        accessor: (r) => (
          <>
            <div className="font-medium">
              {r.service.name} - {r.final_price ? `$${r.final_price.toFixed(2)}` : "Price TBD"}
            </div>
            <div className="text-sm text-muted-foreground">{r.service.parent_name}</div>
          </>
        ),
      },
      {
        header: "Scheduled",
        accessor: (r) => moment(r.date).format("MM/DD/YYYY"),
        sortable: true,
        sortKey: "date",
      },
      {
        header: "Created",
        accessor: (r) => moment(r.created_at).format("MM/DD/YYYY"),
        sortable: true,
        sortKey: "created_at",
      },
      {
        header: "Status",
        accessor: (r) => {
          const metaData = (r.meta_data || {}) as AppointmentMetaData;
          const hasReassignmentRequest =
            r.status === appointment_status.pending &&
            metaData.reassignment_requested_by &&
            metaData.reassignment_request_reason;

          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <AppointmentStatusBadge status={r.status} />
                {hasReassignmentRequest && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-4 h-4 text-amber-600 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reassignment Requested - {metaData.reassignment_request_reason}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <BillingExceptionNote billingException={r.billing_exception} />
            </div>
          );
        },
      },
      {
        header: "Actions",
        accessor: (row) => (
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuickViewAppt(row);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Quick view</TooltipContent>
            </Tooltip>
            <VendorAppointmentActionsDropdown
              appointment={row}
              type="actions"
              onAppointmentUpdate={updateAppointmentInList}
              onRefetch={refetch}
            />
          </div>
        ),
        className: "whitespace-nowrap",
      },
    ],
    [setQuickViewAppt, refetch, updateAppointmentInList]
  );

  return (
    <MainLayout
      breadcrumb={[{ name: "Appointments", href: "#" }]}
      pageHeader={{
        title: "Appointments",
        subtitle: "Manage your vendor appointments here",
        buttonText: "Book Appointment",
        onButtonClick: openSheet,
        additionalButtons: (
          <Button size="lg" variant="outline" onClick={() => setBulkUploadOpen(true)} className="ml-2 uppercase">
            <Upload className="h-4 w-4 mr-2" />
            Bulk Upload
          </Button>
        ),
      }}
    >
      <BookingSheet sheetOpen={sheetOpen} closeSheet={closeSheet} onAppointmentCreated={refetch} />

      {bulkUploadOpen && (
        <BulkUploadModal
          isOpen={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
          onUploadComplete={refetch}
        />
      )}

      <SimpleTable<AppointmentJoin>
        data={list}
        totalCount={total}
        columns={cols}
        page={pagination.page}
        pageSize={pagination.pageSize}
        setPage={pagination.setPage}
        setPageSize={pagination.setPageSize}
        sortKey={filters.sortKey}
        sortOrder={filters.sortOrder}
        onSortChange={filters.setSortKey}
        filters={filterItems}
        getRowClassName={(row) => {
          const metaData = (row.meta_data || {}) as AppointmentMetaData;
          const hasReassignmentRequest =
            row.status === appointment_status.pending &&
            metaData.reassignment_requested_by &&
            metaData.reassignment_request_reason;
          return hasReassignmentRequest ? "bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-950/30" : "";
        }}
        onReset={() => {
          filters.setStatus("");
          filters.setServiceParent("");
          filters.setSearch("");
          filters.setAssignee("");
          filters.setDateFrom("");
          filters.setDateTo("");
        }}
      />

      <AppointmentQuickViewSheet
        appointment={quickViewAppt}
        open={!!quickViewAppt}
        onClose={() => setQuickViewAppt(null)}
        userRole="vendor"
        assignmentSlot={
          quickViewAppt ? (
            <VendorAppointmentActionsDropdown
              appointment={quickViewAppt}
              type="assignment"
              onAppointmentUpdate={(updated) => {
                updateAppointmentInList(updated);
                setQuickViewAppt(updated);
              }}
              onRefetch={refetch}
            />
          ) : undefined
        }
      />
    </MainLayout>
  );
}
